import type * as React from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Link from '@cloudscape-design/components/link';
import Table from '@cloudscape-design/components/table';
import type { Payment } from '@etiquetachef/types';
import { MoneyDisplay } from '@/components/MoneyDisplay';
import { PaymentStatusBadge } from '@/components/PaymentStatusBadge';
import { formatDateTime } from '@/lib/format';

type Props = {
  payments: Payment[];
  locale: string;
};

/**
 * Tabela cross-tenant de pagamentos de `/finance/transactions`, com links para o tenant e a
 * fatura.
 *
 * Origem: sem issue — commit 973be8d9c, 2026-05-05 12:08 +0200 (Sprint 5 PR H).
 *
 * Contrato: só apresentação de `Payment[]`; `amount` em CENTAVOS (`MoneyDisplay` divide). Data
 * = `paid_at`, ou `created_at` quando ainda não pago. `provider` nulo = pagamento manual.
 *
 * Usado por: `routes/TransactionsPage.tsx` (web `apps/admin`).
 *
 * Dados: collection group `payments` — `PaymentSchema` (`packages/types/src/schemas.ts`).
 * `purpose == ADDON_PRORATION` (#737) não tem fatura nossa: o `invoice_id` é a Checkout
 * Session, e o link daria em página vazia — mostra o rótulo "proporção" no lugar.
 *
 * Issues: #737 (proporção avulsa do pós-venda).
 *
 * Histórico:
 *  - 2026-06-08 22:54 +0200 — (39-F) migração para Cloudscape.
 *  - 2026-09-25 10:27 +0200 — (#737) célula de fatura para proporção avulsa.
 */
export function TransactionsTable({ payments, locale }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  /** Link Cloudscape que navega pelo React Router (sem recarregar). */
  const navLink = (href: string, label: string): React.JSX.Element => (
    <Link
      href={href}
      onFollow={(e): void => {
        e.preventDefault();
        void navigate(href);
      }}
    >
      <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{label}</span>
    </Link>
  );

  return (
    <Table<Payment>
      items={payments}
      trackBy="id"
      variant="embedded"
      empty={<Box textAlign="center" color="inherit">{t('admin.finance.transactions.empty')}</Box>}
      columnDefinitions={[
        {
          id: 'date',
          header: t('admin.finance.transactions.columns.date'),
          cell: (p): React.ReactNode =>
            p.paid_at !== null ? formatDateTime(p.paid_at, locale) : formatDateTime(p.created_at, locale),
        },
        {
          id: 'tenant',
          header: t('admin.finance.transactions.columns.tenant'),
          cell: (p): React.ReactNode => navLink(`/tenants/${p.tenant_id}`, p.tenant_id),
        },
        {
          id: 'invoice',
          header: t('admin.finance.transactions.columns.invoice'),
          cell: (p): React.ReactNode =>
            // Proporção avulsa do pós-venda (#737): não há fatura nossa — o
            // `invoice_id` é a Checkout Session, e o link daria em página vazia.
            p.purpose === 'ADDON_PRORATION'
              ? t('admin.finance.transactions.proration')
              : navLink(`/billing/invoices/${p.tenant_id}/${p.invoice_id}`, p.invoice_id),
        },
        {
          id: 'amount',
          header: t('admin.finance.transactions.columns.amount'),
          cell: (p): React.ReactNode => (
            <MoneyDisplay amount={p.amount} currency={p.currency} locale={locale} />
          ),
        },
        {
          id: 'status',
          header: t('admin.finance.transactions.columns.status'),
          cell: (p): React.ReactNode => <PaymentStatusBadge status={p.status} />,
        },
        {
          id: 'provider',
          header: t('admin.finance.transactions.columns.provider'),
          cell: (p): React.ReactNode => (
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
              {p.provider ?? t('admin.finance.transactions.manual')}
            </span>
          ),
        },
        {
          id: 'method',
          header: t('admin.finance.transactions.columns.method'),
          cell: (p): React.ReactNode => (
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.payment_method_type}</span>
          ),
        },
      ]}
    />
  );
}
